import { ColorTypes, utils } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CsvArrayStream } from "@reporting/rp-utils-csv";
import { html } from "lit";
import type { Service } from "../client/service";
import rp from "../global";
import { ToolbarItemOptions, SqlParseResult, SqlError } from "../types";
export * from "../client/sqlStream";
import { readResultStream, SqlDataset, SqlExecCallbackArgs } from "../client/sqlStream";
import { CodeEditorAdapter, CodeEditorOptions, ContentOptions } from "./codeEditorAdapter";
import { CodeCompletionProvider, createSqlCompletionProvider, SqlCompletionOptions, SqlDatabaseRole, SqlSchemaCatalog } from "@reporting/rp-utils-sql-completion";
import { getEditorSettings } from "./editorSettings";
import { SqlEditor } from "./sqlEditor";
import { getSqlSchemaCatalogSource } from "./sqlSchemaCatalog";

export type SqlExecOptions = {
	top?: number,
	queryPlan?: boolean,
	database?: string,
};

export type SqlExportFormat = 'csv' | 'xlsx';


export type SqlEditorOptions = CodeEditorOptions & {
	toolbar?: {
		save?: ToolbarItemOptions,
		discard?: ToolbarItemOptions,
		exec?: ToolbarItemOptions,
		explain?: ToolbarItemOptions,
		compile?: ToolbarItemOptions,
		stop?: ToolbarItemOptions,
		database?: ToolbarItemOptions,
		top?: ToolbarItemOptions,
	}
}

const DEFAULT_SQL_EDITOR_OPTIONS: SqlEditorOptions = {
	language: 'sql',
	toolbar: {
		exec: {
			ord: 0,
			color: ColorTypes.Gray,
			icon: {icon: ['fas', 'play'], color: '#00d100'},
			enabled: true,
			type: 'dropdown-button',
			id: 'exec',
			text: 'Execute',
			title: '(Ctrl+B)',
			width: 90,
			items: [{
				key: 'explain',
				value: 'Explain',
				icon: {icon: ['fas', 'play'], statusIcon: 'graduation-cap', statusColor: '#00d100'}
			}, {
				key: 'compile',
				value: 'Compile',
				icon: {icon: ['fas', 'play'], statusIcon: 'gear', statusColor: '#00d100'}
			}]
		},
		stop: {
			type: 'button',
			hidden: true,
			ord: 1,
			title: 'Stop',
			color: ColorTypes.Yellow,
			content: html`
				<div slot="content" class="rp-editor-stop" style="white-space: nowrap; padding: 0 4px">
					<style>
						.rp-editor-stop og-loader { display: inline-block; }
						.rp-editor-stop og-icon { display: none; color: var(--og-colortype-red-background-color, #ef5350); }
						og-toolbar-button:hover .rp-editor-stop og-loader { display: none; }
						og-toolbar-button:hover .rp-editor-stop og-icon { display: inline-flex; }
					</style>
					<og-loader icon="gear"></og-loader>
					<og-icon .icon="${{ icon: ['fas', 'stop'] }}"></og-icon>
					<span id="duration"></span>
				</div>
			`,
			enabled: true
		},
		database: {
			ord: 2,
			type: 'dropdown',
			id: 'db',
			width: 150,
			enabled: false,
		},
		top: {
			ord: 3,
			type: 'dropdown',
			id: 'top',
			width: 85,
			items: [
				{k: '100', v: 'Top 100'},
				{k: '1000', v: 'Top 1000', s: true},
				{k: '0', v: 'Max'},
			]
		},
		save: {
			ord: 4,
			type: 'button',
			text: 'Save changes',
			title: '(Ctrl+S)',
			color: ColorTypes.Green,
			icon: 'save',
			enabled: true
		},
		discard: {
			ord: 5,
			type: 'button',
			text: 'Discard changes',
			color: ColorTypes.Red,
			icon: 'floppy-disk-circle-xmark',
			enabled: true
		}
	}
}


export type SqlCompileResult = {
	sql: string,
	/** number of lines the application prepends before the user's code */
	offset?: number,
	/** number of lines the application appends after the user's code (computed client-side when missing) */
	tail?: number
};

/**
 * Number of footer lines the application appended after the user's code in the compiled sql
 */
export function computeCompileTail(source: string, compiled: string, offset: number) : number {
	if (!compiled || offset <= 0) return 0;
	const lines = compiled.split('\n');
	const isFooterLine = (line: string) => /^\s*$/.test(line) || /^drop table \[#/i.test(line);
	const expected = lines.length - offset - (source ? source.split('\n').length : 0);
	let tail = 0;
	while (tail < expected && isFooterLine(lines[lines.length - 1 - tail])) tail++;
	return tail;
}



export abstract class SqlEditorAdapter extends CodeEditorAdapter<SqlEditor> {

	public options: SqlEditorOptions = {};
	protected spid: number;
	protected lastExec: { sql: string, options?: SqlExecOptions, params?: Record<string, unknown> } = null;

	abstract getContent(opt?: ContentOptions) : string|Promise<string>;
	abstract save(sql: string) : void|Promise<void>;

	exec(sql: string, options?: SqlExecOptions, cb?: (args: SqlExecCallbackArgs) => void) : void|Promise<void> {
		console.info('exec not implemented');
	}

	export(format: SqlExportFormat, datasetCount: number) : void|Promise<void> {
		console.info('export not implemented');
	}

	compile(sql: string) : string|SqlCompileResult|Promise<string|SqlCompileResult> {
		console.info('compile not implemented');
		return '';
	}

	parse(sql: string) : void|Promise<SqlParseResult> {
		console.info('parse not implemented');
	}

	abort() : void|Promise<void> {
		console.info('abort not implemented');
	}

	/**
	 * Streams a POST exec of a microservice into the editor callback (rp-ms-etl / rp-ms-server protocol:
	 * X-Spid header, tab-separated CSV with JSON header lines). A transport or HTTP error is delivered
	 * to the callback as an error message. Returns whether any dataset, message or plan came back.
	 */
	protected async execStream(
		client: Service,
		path: string,
		sql: string,
		options?: SqlExecOptions,
		cb?: (args: SqlExecCallbackArgs) => void,
		params: Record<string, unknown> = {}
	) : Promise<boolean> {
		let response: Response;
		try {
			response = await client.postRaw(path, sql, {
				top: options?.top,
				// every exec route defaults a missing query_plan to false, so it goes out only when asked for
				...(options?.queryPlan ? { query_plan: 1 } : {}),
				flush_headers: 1,
				csvParams: {
					delimiter: '\t',
					quote: '"',
					header: true,
					headerJson: true,
					nullValue: 'NULL'
				},
				...params
			}, { headers: { accept: 'text/csv' } });
			if (!response.ok) {
				await client.handleResponse(response);
				throw new Error(`HTTP ${response.status}`);
			}
		} catch (error) {
			if (cb) cb({ message: { index: 0, errors: [{ error: error instanceof Error ? error.message : String(error) }] } });
			return false;
		}

		this.spid = parseInt(response.headers.get('X-Spid')) || this.spid;

		const reader = response.body
			.pipeThrough(new CsvArrayStream({ quote: '"', delimiter: '\t', nullValue: 'NULL' }))
			.getReader();
		let received = 0;
		await readResultStream(reader, args => {
			if (args.spid) this.spid = args.spid;
			if (args.dataset || args.message || args.queryPlan) received++;
			if (cb) cb(args);
		});
		return received > 0;
	}

	/**
	 * Kills the session of the last execStream() through the service's abort/{spid} endpoint. A refused
	 * KILL (foreign session, missing permission, query already finished) would otherwise look exactly
	 * like a broken button, so its message is shown.
	 */
	protected async abortSpid(client: Service) : Promise<void> {
		if (!this.spid) return;
		const res = await client.get<{killed: boolean, message?: string}>(`abort/${this.spid}`);
		if (res?.message) {
			rp.host.showAlert({
				title: msg('Stop'),
				content: res.message,
				type: res.killed ? 'info' : 'warning'
			});
		}
	}

	/**
	 * Options of the shared T-SQL completion for the place of use, e.g. `{ items: () => this.getParameterItems() }`
	 * for parameters, or `{ internalVariables: false, macros: false }` where the SQL runs outside the ETL engine.
	 * The user's editor settings (keyword case, signatures, database objects) are applied by getCompletionProvider().
	 */
	getCompletionOptions(): SqlCompletionOptions {
		return {};
	}

	/**
	 * Database the names without a `{db_*}.` prefix resolve in, for the database objects of the completion: the
	 * customer data database unless the SQL runs elsewhere (a deployment thread of the super database); null offers
	 * the objects only behind a placeholder.
	 */
	getSchemaDatabase(): SqlDatabaseRole | null {
		return 'data';
	}

	/**
	 * The database objects of the completion: the static catalogue of the repository with the live objects of the
	 * company's databases merged in as they arrive (see sqlSchemaCatalog.ts). An editor whose SQL does not run in
	 * the company's databases returns the static SQL_SCHEMA_CATALOG.
	 */
	getSchemaCatalog(): SqlSchemaCatalog | (() => SqlSchemaCatalog) {
		return getSqlSchemaCatalogSource();
	}

	/** Shared T-SQL completion (keywords, functions, @@variables, types, snippets, database objects) as the user set it up. */
	getCompletionProvider(): null | CodeCompletionProvider | Promise<null | CodeCompletionProvider> {
		const settings = getEditorSettings();
		return createSqlCompletionProvider({
			keywordCase: settings.keywordCase,
			signatures: settings.signatures,
			schema: settings.schema ? { database: this.getSchemaDatabase(), catalog: this.getSchemaCatalog() } : false,
			...this.getCompletionOptions(),
		});
	}

	createOptions(options: Partial<SqlEditorOptions>): SqlEditorOptions {
		const mergedOptions = utils.mergeDeep({}, DEFAULT_SQL_EDITOR_OPTIONS, options);
		// fix HTML template after merge
		if (options.toolbar?.stop?.content) {
			mergedOptions.toolbar.stop.content = options.toolbar.stop.content;
		} else if (DEFAULT_SQL_EDITOR_OPTIONS.toolbar?.stop?.content) {
			mergedOptions.toolbar.stop.content = DEFAULT_SQL_EDITOR_OPTIONS.toolbar.stop.content;
		}

		return this.options = mergedOptions;
	}

	getOptions() : SqlEditorOptions|Promise<SqlEditorOptions> {
		return DEFAULT_SQL_EDITOR_OPTIONS;
	}

}
